import { toast } from "react-toastify";
import { useCallback, useEffect, useMemo, useState } from "react";

import { actValidationMessage, formatCourtDate, formatActDate } from "@/Utils/litigationDates.mjs";

import { useLitigationDatePreviews } from "../../LitigationDashboard/useLitigationDateCalculations";
import { getCaseId, getClientId, toTitleCase } from "../../../Utils/helper";
import { getDateEndpointLabel } from "../../../Utils/litigationDateLayout";
import { api_without_cancellation } from "../../../api/api";
import SOLicon from "../../../assets/litigation/sol.svg";
// import LitigationPartySelect, {
//   isSameLitigationParty,
// } from "../../LitigationDashboard/LitigationPartySelect";

const EMPTY_FORM_DATA = {
  date: "",
  act_id: "",
  for_service: "",
  server: null,
  served: null,
};

const getTemplate = (act) =>
  act?.event_id && typeof act.event_id === "object" ? act.event_id : act;

const getConfigurationValue = (act, key) => act?.[key] ?? getTemplate(act)?.[key];

const getDependantEndpoint = (act) =>
  getConfigurationValue(act, "is_dependant_date") ? "start_date" : "end_date";

const getTriggerEvents = (act, key) => {
  const events = getConfigurationValue(act, key);
  return Array.isArray(events) ? events : [];
};

const getAdditionalEvents = (act) => {
  const dependantDateName = getConfigurationValue(act, "is_dependant_date")
    ? getConfigurationValue(act, "start_date_label") ||
      getConfigurationValue(act, "dependent_date_type_id")?.dependent_date_name ||
      act?.dependant_date_type?.dependent_date_name ||
      act?.name ||
      "Dependant Date"
    : getConfigurationValue(act, "end_date_label") ||
      getConfigurationValue(act, "dependent_date_type_id")?.dependent_date_name ||
      act?.dependant_date_type?.dependent_date_name ||
      act?.name ||
      "Dependant Date";

  const triggerGroups = [
    {
      events: getTriggerEvents(act, "trigger_on_accept"),
      label: "Acceptance",
      triggerType: "accept",
    },
    {
      events: getTriggerEvents(act, "trigger_on_reject"),
      label: "Rejection",
      triggerType: "reject",
    },
    {
      events: getTriggerEvents(act, "trigger_on_deadline"),
      label: "Deadline Expiry",
      triggerType: "deadline",
    },
    {
      events: getTriggerEvents(act, "trigger_on_dependant_date"),
      label: dependantDateName,
      triggerType: "dependant",
    },
  ];

  return triggerGroups.flatMap(({ events, label, triggerType }) =>
    events.map((event) => ({ event, triggerLabel: label, triggerType }))
  );
};

const combineParties = (responseData) => {
  const parties = [];

  (responseData?.clients || []).forEach((client) => {
    parties.push({
      id: client.id,
      type: "Client",
      name: `${client?.first_name || ""} ${client?.last_name || ""}`.trim(),
    });
  });

  (responseData?.defendants || []).forEach((defendant) => {
    const isIndividual = Number(defendant?.defendantType?.id) === 1;
    const defendantName = isIndividual
      ? `${defendant?.first_name || ""} ${defendant?.last_name || ""}`.trim()
      : defendant?.entity_name || "";

    parties.push({
      id: defendant.id,
      type: "Defendant",
      name: defendantName,
    });
  });

  (responseData?.other_parties || []).forEach((party) => {
    parties.push({
      id: party.id,
      type: "OtherParty",
      name: `${party?.party_first_name || party?.first_name || ""} ${
        party?.party_last_name || party?.last_name || ""
      }`.trim(),
    });
  });

  return parties;
};

const getStoredParty = (act, role, partiesData) => {
  const storedParty = act?.service?.[`${role}_object`] || act?.[role] || null;
  const storedContentType = act?.service?.[`${role}_content_type`] || storedParty?.type || "";

  if (!storedParty?.id) return null;

  return (
    partiesData.find(
      (party) =>
        Number(party.id) === Number(storedParty.id) &&
        (!storedContentType || party.type.toLowerCase() === String(storedContentType).toLowerCase())
    ) || null
  );
};

const getInitialFormData = (act, partiesData) => ({
  ...EMPTY_FORM_DATA,
  act_id: act.id,
  for_service: act?.for_service?.id || "",
  server: getStoredParty(act, "server", partiesData),
  served: getStoredParty(act, "served", partiesData),
});

const getEventId = (event) => (event && typeof event === "object" ? event.id : event);

const getEventTypeIcon = (event, eventTypesData) => {
  const eventTypeId =
    event?.event_type_id && typeof event.event_type_id === "object"
      ? event.event_type_id.id
      : event?.event_type_id;
  const eventType = eventTypesData?.find((type) => Number(type.id) === Number(eventTypeId));
  const eventTypeName =
    eventType?.litigation_event_type || event?.event_type_id?.litigation_event_type || "";

  if (!eventTypeName) return null;

  if (eventTypeName === "Statute of Limitations") {
    return <img src={SOLicon} alt="" className="ic-15 m-r-5" />;
  }

  return (
    <i
      className={`ic ic-15 m-r-5 ic-${
        eventTypeName === "Discovery" || eventTypeName === "Expert Discovery"
          ? "discovery"
          : eventTypeName
      }`}
    />
  );
};

export const useLitigationCentralDateEntry = ({ data, fetchLitigationActs }) => {
  const [formData, setFormData] = useState(EMPTY_FORM_DATA);
  const [savingId, setSavingId] = useState(null);
  const [serviceData, setServiceData] = useState([]);
  const [partiesData, setPartiesData] = useState([]);

  const needsServiceData = data.some(
    (act) =>
      Boolean(getConfigurationValue(act, "service_method")) ||
      Boolean(getConfigurationValue(act, "to_from"))
  );
  const stateAbbreviation = data?.litigation?.state?.StateAbr || "";

  useEffect(() => {
    if (!needsServiceData) return undefined;

    let cancelled = false;

    const fetchEntryOptions = async () => {
      try {
        const requests = [
          api_without_cancellation.get("/api/litigation-page/service-methods/", {
            ...(stateAbbreviation ? { params: { state: stateAbbreviation } } : {}),
          }),
          api_without_cancellation.get(
            `/api/litigation-page/defendants-and-other-parties/${getClientId()}/${getCaseId()}/`
          ),
        ];
        const [serviceResponse, partiesResponse] = await Promise.all(requests);

        if (!cancelled) {
          setServiceData(serviceResponse.data || []);
          setPartiesData(combineParties(partiesResponse.data));
        }
      } catch (error) {
        console.error("Error fetching dependant date service options:", error);
      }
    };

    fetchEntryOptions();

    return () => {
      cancelled = true;
    };
  }, [needsServiceData, stateAbbreviation]);

  const updateFormData = useCallback(
    (act, changes) => {
      setFormData((currentFormData) => ({
        ...(currentFormData.act_id === act.id
          ? currentFormData
          : getInitialFormData(act, partiesData)),
        act_id: act.id,
        ...changes,
      }));
    },
    [partiesData]
  );

  const activeAct = useMemo(
    () => data.find((act) => act.id === formData.act_id) || null,
    [data, formData.act_id]
  );

  const previewRequests = [];
  if (activeAct && formData.date) {
    const serviceMethodId =
      formData.for_service ||
      activeAct?.for_service?.id ||
      getConfigurationValue(activeAct, "service_method_id") ||
      null;
    (activeAct.calculated_dates || []).forEach((date) =>
      previewRequests.push({
        key: activeAct.id + ":calculated-date-" + date.id,
        calculatedDateId: date.id,
        startDate: formData.date,
        serviceMethodId,
      })
    );
    const endpoint = getDependantEndpoint(activeAct);
    getAdditionalEvents(activeAct).forEach(({ event, triggerType }) => {
      const startDate =
        triggerType === "dependant"
          ? formData.date
          : triggerType === "deadline"
            ? endpoint === "end_date"
              ? formData.date
              : activeAct.end_date
            : null;
      if (startDate)
        previewRequests.push({
          key: activeAct.id + ":trigger-" + triggerType + "-" + getEventId(event),
          eventId: getEventId(event),
          startDate,
          serviceMethodId: triggerType === "deadline" ? null : serviceMethodId,
        });
    });
  }
  const calculatedDatePreviews = useLitigationDatePreviews(previewRequests, Boolean(activeAct));

  const setDependantDate = useCallback(
    async (act) => {
      if (formData.act_id !== act.id || !formData.date) return;

      setSavingId(act.id);
      try {
        const endpoint = getDependantEndpoint(act);
        const usesServiceMethod = Boolean(getConfigurationValue(act, "service_method"));
        const usesToFrom = Boolean(getConfigurationValue(act, "to_from"));
        const storedFormData = getInitialFormData(act, partiesData);
        const effectiveForService = formData.for_service || storedFormData.for_service;
        const effectiveServer = formData.server || storedFormData.server;
        const effectiveServed = formData.served || storedFormData.served;
        const response = await api_without_cancellation.put(
          "/api/litigation-page/litigation-act-for-litigation-central/",
          {
            act_id: act.id,
            [endpoint]: formData.date,
            ...(usesServiceMethod && effectiveForService
              ? { for_service: Number(effectiveForService) }
              : {}),
            ...(usesToFrom && effectiveServer ? { server: effectiveServer } : {}),
            ...(usesToFrom && effectiveServed ? { served: effectiveServed } : {}),
          }
        );

        if (response.status === 200) {
          await fetchLitigationActs?.();
          setFormData(EMPTY_FORM_DATA);
        }
      } catch (error) {
        console.error("Error setting dependant date:", error);
        toast.error(actValidationMessage(error));
      } finally {
        setSavingId(null);
      }
    },
    [fetchLitigationActs, formData, partiesData]
  );

  return {
    calculatedDatePreviews,
    formData,
    partiesData,
    savingId,
    serviceData,
    setDependantDate,
    updateFormData,
  };
};

const DatePreview = ({ date }) =>
  date ? <div className="ms-2 fw-normal text-nowrap">{formatCourtDate(date)}</div> : null;

export const LitigationCentralCalculationCell = ({ act, entry, eventTypesData }) => {
  const additionalEvents = getAdditionalEvents(act);

  return (
    <td className="d-flex flex-column">
      {act?.calculated_dates?.length > 0 ? (
        act.calculated_dates.map((calculatedDate) => (
          <div key={calculatedDate.id} className="d-flex justify-content-between align-items-start">
            <div className="d-flex align-items-center">
              {getEventTypeIcon(calculatedDate, eventTypesData)}
              {toTitleCase(calculatedDate?.calculated_date_name)}:
            </div>
            <DatePreview
              date={entry.calculatedDatePreviews[`${act.id}:calculated-date-${calculatedDate.id}`]}
            />
          </div>
        ))
      ) : (
        <div>No Calculated Dates</div>
      )}

      {additionalEvents.map(({ event, triggerLabel, triggerType }) => {
        const eventId = getEventId(event);
        const eventName = event?.event_name || "Additional Event";

        return (
          <div
            key={`${triggerType}-${eventId}`}
            className="d-flex justify-content-between align-items-start"
          >
            <div className="d-flex align-items-center">
              {getEventTypeIcon(event, eventTypesData)}
              <span>{toTitleCase(eventName)}:</span>
            </div>

            {entry.calculatedDatePreviews[`${act.id}:trigger-${triggerType}-${eventId}`] ? (
              <DatePreview
                date={entry.calculatedDatePreviews[`${act.id}:trigger-${triggerType}-${eventId}`]}
              />
            ) : (
              <span className="fw-normal text-primary-25 ms-1">
                Triggered on {toTitleCase(triggerLabel)}
              </span>
            )}
          </div>
        );
      })}
    </td>
  );
};

export const LitigationCentralDependantDateCell = ({ act, entry }) => {
  const isActive = entry.formData.act_id === act.id;
  const dependantEndpoint = getDependantEndpoint(act);
  const dependantDateLabel = getDateEndpointLabel(
    act,
    dependantEndpoint,
    act?.dependant_date_type?.dependent_date_name || "Dependant Date"
  );
  // const usesServiceMethod = Boolean(getConfigurationValue(act, "service_method"));
  // const usesToFrom = Boolean(getConfigurationValue(act, "to_from"));
  // const storedFormData = getInitialFormData(act, entry.partiesData);
  // const rowFormData = isActive
  //   ? {
  //       ...storedFormData,
  //       ...entry.formData,
  //       for_service: entry.formData.for_service || storedFormData.for_service,
  //       server: entry.formData.server || storedFormData.server,
  //       served: entry.formData.served || storedFormData.served,
  //     }
  //   : storedFormData;
  // const filteredParties = (selectedParty) =>
  //   selectedParty
  //     ? entry.partiesData.filter((party) => !isSameLitigationParty(party, selectedParty))
  //     : entry.partiesData;

  // const handlePartyChange = (field, selectedParty) => {
  //   const otherField = field === "server" ? "served" : "server";
  //   const otherParty = rowFormData[otherField];

  //   entry.updateFormData(act, {
  //     [field]: selectedParty,
  //     ...(isSameLitigationParty(selectedParty, otherParty) ? { [otherField]: null } : {}),
  //   });
  // };

  return (
    <>
      <td>
        <div className="d-flex flex-column m-t-5 m-b-5">
          {act?.uses_start_date &&
            act?.uses_end_date &&
            act?.is_dependant_date &&
            act?.end_date && (
              <div className="d-flex justify-content-center align-items-center">
                <span className="m-r-5">
                  {act.end_date_label ? act.end_date_label : "Deadline"}:
                </span>
                <span className="fw-normal">{formatActDate(act, "end_date")}</span>
              </div>
            )}
          <div className="d-flex justify-content-end align-items-center m-t-5">
            <div>{toTitleCase(dependantDateLabel)}:</div>
            <div className="p-l-5 p-r-5">
              <input
                type="datetime-local"
                name={dependantEndpoint}
                className="form-control height-21"
                value={isActive ? entry.formData.date : ""}
                onChange={(event) => entry.updateFormData(act, { date: event.target.value })}
              />
            </div>
          </div>

          {/* {usesServiceMethod && (
            <div className="d-flex justify-content-end align-items-center m-t-5 p-r-5">
              <label className="p-r-5 text-primary-25 m-0" htmlFor={`service-method-${act.id}`}>
                Service Method:
              </label>
              <select
                id={`service-method-${act.id}`}
                name="for_service"
                className="form-select form-control height-21 py-0"
                style={{ maxWidth: "260px" }}
                value={rowFormData.for_service}
                onChange={(event) => entry.updateFormData(act, { for_service: event.target.value })}
              >
                <option value="">Select Service Method</option>
                {entry.serviceData.map((serviceMethod) => (
                  <option key={serviceMethod.id} value={serviceMethod.id}>
                    {serviceMethod.name} Service adds {serviceMethod.number_of_days || 0} days to
                    the deadline.
                  </option>
                ))}
              </select>
            </div>
          )}

          {usesToFrom && (
            <>
              <div className="d-flex justify-content-end align-items-center m-t-5 p-r-5">
                <label className="p-r-5 text-primary-25 m-0" htmlFor={`served-${act.id}`}>
                  Served:
                </label>
                <LitigationPartySelect
                  id={`served-${act.id}`}
                  name="served"
                  style={{ maxWidth: "260px" }}
                  value={rowFormData.served}
                  options={filteredParties(rowFormData.server)}
                  placeholder={`Select ${toTitleCase(
                    getConfigurationValue(act, "tolabel") || "Serviced"
                  )}`}
                  onChange={(party) => handlePartyChange("served", party)}
                />
              </div>
              <div className="d-flex justify-content-end align-items-center m-t-5 p-r-5">
                <label className="p-r-5 text-primary-25 m-0" htmlFor={`server-${act.id}`}>
                  From:
                </label>
                <LitigationPartySelect
                  id={`server-${act.id}`}
                  name="server"
                  style={{ maxWidth: "260px" }}
                  value={rowFormData.server}
                  options={filteredParties(rowFormData.served)}
                  placeholder={`Select ${toTitleCase(
                    getConfigurationValue(act, "fromlabel") || "Server"
                  )}`}
                  onChange={(party) => handlePartyChange("server", party)}
                />
              </div>
            </>
          )} */}
        </div>
      </td>
      <td>
        <div className="d-flex justify-content-center p-r-10 p-l-10">
          {entry.savingId === act.id ? (
            <button
              type="button"
              className="btn btn-secondary btn-sm height-21 d-flex align-items-center justify-content-center"
              disabled
            >
              <span className="spinner-border spinner-border-sm m-r-5" role="status" />
              Saving...
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary btn-sm height-21 d-flex align-items-center justify-content-center"
              disabled={!isActive || !entry.formData.date}
              onClick={() => entry.setDependantDate(act)}
            >
              Save
            </button>
          )}
        </div>
      </td>
    </>
  );
};
